/**
 * 個別説明記録 — 商談詳細から手動追加するための簡易ダイアログ。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §7.6（2026-05-28 追加: 手動追加経路）
 *
 * 既存の BriefingRecordPage は「スケジュール確定済み Deal の最新記録を編集」フロー前提。
 * 本ダイアログは schedule なしで記録を直接追加するためのもので、saveBriefingRecord callable に
 * briefingScheduleId 未指定で投げる（サーバ側で null 保存）。
 *
 * コンフリクト回避:
 *  - 同じ saveBriefingRecord を使うため Activity / mail_contacts/activities 自動追加は共通
 *  - 既存「日程確定 → 記録ページ」フローと並列で実行可能。重複防止はアプリ側で行う
 */
import type {
  ConversionLevel,
  PaymentMethod,
} from '@goenhub/shared';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { useSaveBriefingRecord } from '@/hooks/useBriefings';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
}

const PAYMENT_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'undecided', label: '未定' },
  { value: 'transfer', label: '銀行振込' },
  { value: 'credit_card', label: 'クレジットカード' },
];

const CONVERSION_LEVELS: ConversionLevel[] = [1, 2, 3, 4, 5];

export function AddBriefingRecordDialog({ open, onOpenChange, dealId }: Props) {
  const saveMutation = useSaveBriefingRecord();

  const [conversionLevel, setConversionLevel] = useState<ConversionLevel>(3);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('undecided');
  const [hasTradeIn, setHasTradeIn] = useState(false);
  const [saleAware, setSaleAware] = useState(false);
  const [hasIncentive, setHasIncentive] = useState(false);
  const [inquiries, setInquiries] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [supplementaryNotes, setSupplementaryNotes] = useState('');

  // 開く度に初期化
  useEffect(() => {
    if (!open) return;
    setConversionLevel(3);
    setPaymentMethod('undecided');
    setHasTradeIn(false);
    setSaleAware(false);
    setHasIncentive(false);
    setInquiries('');
    setNextAction('');
    setSupplementaryNotes('');
  }, [open]);

  const handleSubmit = async () => {
    try {
      await saveMutation.mutateAsync({
        dealId,
        // 明示的に null（手動追加経路の識別キー）
        briefingScheduleId: null,
        conversionLevel,
        paymentMethod,
        hasTradeIn,
        saleAware,
        hasIncentive,
        inquiries: inquiries.trim() || undefined,
        nextAction: nextAction.trim() || undefined,
        supplementaryNotes: supplementaryNotes.trim() || undefined,
      });
      toast.success('記録を追加しました');
      onOpenChange(false);
    } catch (err) {
      toast.error(`追加失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>個別説明記録を追加</DialogTitle>
          <DialogDescription>
            スケジュール確定なしで記録だけを残します。詳細編集が必要な場合は保存後に「記録ページを開く」から続行できます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">成約レベル (1〜5)</Label>
              <Select
                value={String(conversionLevel)}
                onValueChange={(v) =>
                  setConversionLevel(Number(v) as ConversionLevel)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONVERSION_LEVELS.map((lv) => (
                    <SelectItem key={lv} value={String(lv)}>
                      レベル {lv}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">想定支払方法</Label>
              <Select
                value={paymentMethod}
                onValueChange={(v) => setPaymentMethod(v as PaymentMethod)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Switch
                checked={hasTradeIn}
                onChange={(e) => setHasTradeIn(e.target.checked)}
              />
              <span>下取り検討</span>
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Switch
                checked={saleAware}
                onChange={(e) => setSaleAware(e.target.checked)}
              />
              <span>セール認知</span>
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Switch
                checked={hasIncentive}
                onChange={(e) => setHasIncentive(e.target.checked)}
              />
              <span>特典あり</span>
            </label>
          </div>

          <div>
            <Label className="text-xs">問い合わせ内容（任意）</Label>
            <Textarea
              rows={2}
              placeholder="顧客から受けた質問・要望"
              value={inquiries}
              onChange={(e) => setInquiries(e.target.value)}
            />
          </div>

          <div>
            <Label className="text-xs">ネクストアクション（任意）</Label>
            <Textarea
              rows={2}
              placeholder="次に取るべき行動（例: 見積送付 / 再面談）"
              value={nextAction}
              onChange={(e) => setNextAction(e.target.value)}
            />
          </div>

          <div>
            <Label className="text-xs">補足メモ（任意）</Label>
            <Textarea
              rows={2}
              placeholder="その他、商談に関する記録"
              value={supplementaryNotes}
              onChange={(e) => setSupplementaryNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saveMutation.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? (
              <Spinner className="size-3 mr-1" />
            ) : (
              <Save className="size-4 mr-1" />
            )}
            記録を追加
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
